import {
	NavigationGuard,
	RouteLocationNormalized,
	RouteLocationNormalizedGeneric,
	RouteLocationNormalizedLoaded,
	RouteLocationRaw,
	Router,
} from "vue-router";
import { useLogger } from "../composable/logger";
import { useAuthStore } from "../stores";
import { useAppStore } from "../stores/app";
import { useFeaturesStore } from "../stores/features";
import { usePermissionStore } from "../stores/permission";

function authRedirect(to: RouteLocationNormalizedGeneric): RouteLocationRaw {
	return {
		name: "auth-login",
		query: {
			redirect: to.fullPath,
		},
	};
}

function notFoundRedirect(to: RouteLocationNormalizedGeneric): RouteLocationRaw {
	return {
		name: "404",
		params: {
			path: to.path.split("/").slice(1),
		},
	};
}

type SimpleNavigationGuard = (
	to: RouteLocationNormalized,
	from: RouteLocationNormalizedLoaded
) => ReturnType<NavigationGuard>;

function makeGuard(name: string, guard: SimpleNavigationGuard): SimpleNavigationGuard {
	const logger = useLogger();

	function log(msg: string) {
		logger.debug(`Guard (${name}) - ${msg}`);
	}

	return async (to, from) => {
		const result = await guard(to, from);
		if (result != null) {
			log(`Redirecting to ${JSON.stringify(result)}`);
		}

		return result;
	};
}

export function initRouterGuards(router: Router) {
	const guards = [disabledRouteGuard, authGuard, permissionGuard];

	for (const guard of guards) {
		router.beforeResolve(guard);
	}
}

export const authGuard = makeGuard("auth", async (to) => {
	if (to.meta.ignoreAuth) {
		return;
	}

	await useAppStore().waitReady();

	const authStore = useAuthStore();

	if (authStore.user == null) {
		return authRedirect(to);
	}
});

export const disabledRouteGuard = makeGuard("disabled", (to) => {
	if (to.meta.disabled) {
		return notFoundRedirect(to);
	}
});

export const permissionGuard = makeGuard("permission", async (to) => {
	if (to.meta.ignoreAuth) {
		return;
	}

	const { hasUserAccessToRoute } = usePermissionStore();
	const { isRouteActive } = useFeaturesStore();

	await useAppStore().waitReady();

	if (!hasUserAccessToRoute(to) || !isRouteActive(to, "user")) {
		return notFoundRedirect(to);
	}
});
